import { type TablePaginationProps } from "@mui/material";
import { useCallback } from "react";
import { ListPaginationContext, Pagination, PaginationActions, type PaginationActionsProps } from "react-admin";

type ListPaginationProps = {
  page: number;
  perPage: number;
  resource: string;
  total?: number;
  hasNextPage?: boolean;
  onPageChange: (page: number) => void;
  onPerPageChange?: (perPage: number) => void;
  rowsPerPageOptions?: number[];
  isPending?: boolean;
  isLoading?: boolean;
  disabled?: boolean;
};

export const ListPagination = ({
  page,
  perPage,
  resource,
  total,
  hasNextPage = false,
  onPageChange,
  onPerPageChange,
  rowsPerPageOptions = [],
  isPending = false,
  isLoading = isPending,
  disabled = false,
}: ListPaginationProps) => {
  const blocked = isPending || isLoading || disabled;
  const canNext = total == null ? hasNextPage : page * perPage < total;
  // React-Admin's base type omits these forwarded MUI pagination props.
  const controlProps: Pick<TablePaginationProps, "disabled" | "backIconButtonProps" | "nextIconButtonProps"> = {
    disabled: blocked,
    backIconButtonProps: { disabled: blocked || page <= 1 },
    nextIconButtonProps: { disabled: blocked || !canNext },
  };
  const actions = useCallback(
    (props: PaginationActionsProps) => <PaginationActions {...props} disabled={blocked} />,
    [blocked]
  );

  return (
    <ListPaginationContext.Provider
      value={{
        hasNextPage: canNext,
        hasPreviousPage: page > 1,
        isLoading,
        isPending,
        page,
        perPage,
        resource,
        setPage: next => {
          if (!blocked) onPageChange(next);
        },
        setPerPage: next => {
          if (!blocked) onPerPageChange?.(next);
        },
        total,
      }}
    >
      <Pagination
        {...controlProps}
        actions={total == null ? undefined : actions}
        rowsPerPageOptions={rowsPerPageOptions}
      />
    </ListPaginationContext.Provider>
  );
};
